import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useLang } from '../i18n/LanguageContext';
import { useCart } from '../context/CartContext';
import { storefront } from '../lib/supabase';
import { formatVND, pickLang } from '../lib/format';
import OfferProgress from '../components/OfferProgress';

export function useOfferMath(subtotal, tiers) {
  const reached = [...tiers]
    .sort((a, b) => a.min_subtotal - b.min_subtotal)
    .filter((x) => subtotal >= Number(x.min_subtotal))
    .pop();
  const discountPct = Number(reached?.discount_percent) || 0;
  const discount = Math.round((subtotal * discountPct) / 100);
  return { reached, discountPct, discount, total: subtotal - discount };
}

export default function Cart() {
  const { lang, t } = useLang();
  const { items, setQty, remove, subtotal } = useCart();
  const [tiers, setTiers] = useState([]);
  const [products, setProducts] = useState([]);

  useEffect(() => {
    storefront.offerTiers().then(({ data }) => setTiers(data || []));
    storefront.products().then(({ data }) => setProducts(data || []));
  }, []);

  const { discount, total, reached } = useOfferMath(subtotal, tiers);

  if (!items.length) {
    return (
      <div className="container section">
        <h1>🛒 {t('cart.title')}</h1>
        <div className="empty card">
          {t('cart.empty')}
          <div style={{ marginTop: 14 }}>
            <Link to="/products" className="btn btn-primary">{t('cart.continue')}</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container section" style={{ maxWidth: 860 }}>
      <h1>🛒 {t('cart.title')}</h1>

      <div className="card" style={{ padding: 0, marginBottom: 16 }}>
        <table className="tbl">
          <tbody>
            {items.map((x) => (
              <tr key={`${x.kind}-${x.id}`}>
                <td style={{ width: '45%' }}>
                  {x.kind === 'combo' && <span className="badge badge-sale" style={{ marginRight: 6 }}>COMBO</span>}
                  <strong>{lang === 'en' && x.name_en ? x.name_en : x.name}</strong>
                </td>
                <td>
                  <div className="row" style={{ flexWrap: 'nowrap' }}>
                    <button className="qty-btn" onClick={() => setQty(x.kind, x.id, x.qty - 1)}>−</button>
                    <span style={{ minWidth: 22, textAlign: 'center' }}>{x.qty}</span>
                    <button className="qty-btn" onClick={() => setQty(x.kind, x.id, x.qty + 1)}>+</button>
                  </div>
                </td>
                <td style={{ textAlign: 'right' }}><span className="price">{formatVND(x.price * x.qty)}</span></td>
                <td style={{ width: 60 }}>
                  <button className="btn btn-danger btn-sm" onClick={() => remove(x.kind, x.id)}>✕</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div style={{ marginBottom: 16 }}>
        <OfferProgress tiers={tiers} products={products} />
      </div>

      <div className="card">
        <div className="row spread"><span>{t('cart.subtotal')}</span><strong>{formatVND(subtotal)}</strong></div>
        {discount > 0 && (
          <div className="row spread" style={{ color: 'var(--green)' }}>
            <span>{t('cart.discount')} ({pickLang(reached, 'reward', lang)})</span>
            <strong>−{formatVND(discount)}</strong>
          </div>
        )}
        <hr style={{ border: 'none', borderTop: '1px solid var(--hairline-soft)' }} />
        <div className="row spread" style={{ fontSize: 18 }}>
          <strong>{t('cart.total')}</strong>
          <strong className="price">{formatVND(total)}</strong>
        </div>
        <div className="row" style={{ marginTop: 14 }}>
          <Link to="/checkout" className="btn btn-primary">{t('cart.checkout')}</Link>
          <Link to="/products" className="btn btn-secondary">{t('cart.continue')}</Link>
        </div>
      </div>
    </div>
  );
}
